import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, of, switchMap } from 'rxjs';
import { FormulaHelperService } from '../../services/formula-helper.service';
import {
    PayBillingActions,
    MarginCalculatorPageActions as PageActions,
    MarginCalculatorApiActions as ApiActions,
    HousingActions,
    MarginCalculatorComputationActions as ComputationActions,
    MarginCalculatorApiActions
} from '../actions';
import * as Selectors from '../selectors';
import { PayBillingStateHelpers } from '../state/pay-billing-state-helper';
import Big from 'big.js';
import { MarginCalculatorFormService } from '../../services/margin-calculator-form.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

@Injectable()
export class PayRateEffects {
    calculatePayRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PageActions.marginCalculatorComponentLoaded),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginPayRate)]),
            concatMap(([, payRate]) => {
                return of(PayBillingActions.setMarginPayRate({ payRate }));
            })
        );
    });

    calculateMinPayRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setMarginPayRate),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginPayRate)]),
            concatMap(([, payrate]) => {
                const minPremiumPayRate = FormulaHelperService.calculateMinPremiumPayRate(payrate);
                return of(PayBillingActions.setMinPremiumPayRates({ rate: minPremiumPayRate }));
            })
        );
    });

    calculateMinPayRateOnManualChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PageActions.onMarginPayRateChanged),
            concatLatestFrom(() => [this.store.select(Selectors.selectMarginPayRate)]),
            concatMap(([, payrate]) => {
                const minPremiumPayRate = FormulaHelperService.calculateMinPremiumPayRate(payrate);
                return of(PayBillingActions.setMinPremiumPayRateOnly({ rate: minPremiumPayRate }));
            })
        );
    });

    calculatePremiumPayRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setMinPremiumPayRates, PayBillingActions.setMinPremiumPayRateOnly),
            concatLatestFrom(() => [this.store.select(Selectors.selectMinPremiumPayRate)]),
            concatMap(([, payRate]) => {
                return of(PayBillingActions.setPremiumRate({ payRate }));
            })
        );
    });

    calculateCallBackHolidayPayRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setMinPremiumPayRates, PayBillingActions.setCallbackIncluded),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMinPremiumPayRate),
                this.store.select(Selectors.selectMarginExists),
                this.store.select(Selectors.selectFirstBillRateIncludeCallback)
            ]),
            concatMap(([, minPayRate, marginExists, facilityCallbackPayIncluded]) => {
                if (!marginExists && !facilityCallbackPayIncluded) {
                    return of(
                        PayBillingActions.holidayCallbackPayRateChanged({
                            callbackPayRate: 0,
                            holidayPayRate: minPayRate
                        })
                    );
                }
                return of(
                    PayBillingActions.holidayCallbackPayRateChanged({
                        callbackPayRate: minPayRate,
                        holidayPayRate: minPayRate
                    })
                );
            })
        );
    });

    calculateOnCallPayRateFromBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.onCallBillRateChanged, PayBillingActions.recalculateOnCallBillRateSuccess),
            concatLatestFrom(() => [this.store.select(Selectors.selectOnCallBillRate)]),
            switchMap(([, onCallBillRate]) => {
                const newPayRate = new Big(onCallBillRate || 0).div(2).toNumber();
                return of(PayBillingActions.onCallPayRateChanged({ onCallPayRate: newPayRate }));
            })
        );
    });

    calculateOnCallBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate,
                PageActions.setShiftHours,
                ComputationActions.baseBillingRateComputedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectExpectedOnCallBillRate),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, onCallBillRate, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(PayBillingActions.recalculateOnCallBillRateSuccess({ onCallBillRate }));
                }

                return of(PageActions.noOp({ debug: 'calculateOnCallBillRate$ effect' }));
            })
        );
    });

    calculateChargeBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate,
                PageActions.setShiftHours,
                ComputationActions.baseBillingRateComputedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectExpectedChargeBillRate),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, chargeBillRate, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(PayBillingActions.recalculateChargeBillRateSuccess({ chargeBillRate }));
                }

                return of(PageActions.noOp({ debug: 'calculateChargeBillRate$ effect' }));
            })
        );
    });

    setChargePayRateFromChargeBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.chargeBillRateChanged, PayBillingActions.recalculateChargeBillRateSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectChargeBillRate),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, chargeBillRate, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    const chargePayRate = new Big(chargeBillRate).div(2).round(2, Big.roundHalfUp).toNumber();
                    return of(PayBillingActions.chargePayRateChanged({ chargePayRate }));
                }
                return of(PageActions.noOp({ debug: 'calculateOnCallPayRateFromBillRate$ effect' }));
            })
        );
    });

    calculateHolidayBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                PageActions.setShiftHours,
                ComputationActions.baseBillingRateComputedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectExpectedHolidayBillRate),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, holidayBillRate, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(PayBillingActions.recalculateHolidayBillRateSuccess({ holidayBillRate }));
                }

                return of(PageActions.noOp({ debug: 'calculateHolidayBillRate$ effect' }));
            })
        );
    });

    calculateCallbackBillRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                PageActions.setShiftHours,
                ComputationActions.baseBillingRateComputedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectExpectedCallbackBillRate),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, callbackBillRate, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(PayBillingActions.recalculateCallbackBillRateSuccess({ callbackBillRate }));
                }

                return of(PageActions.noOp({ debug: 'calculateCallbackBillRate$ effect' }));
            })
        );
    });

    setCallbackIncluded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                ComputationActions.baseBillingRateComputedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFirstBillRateIncludeCallback),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, facilityCallbackIncluded, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(
                        PayBillingActions.setCallbackIncluded({ callbackPayRateIncluded: facilityCallbackIncluded })
                    );
                }

                return of(PageActions.noOp({ debug: 'setCallbackIncluded$ effect' }));
            })
        );
    });

    resetHousingStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HousingActions.setHousingSelected),
            switchMap(() => {
                return of(PayBillingActions.setHousingStipend({ housingStipend: 0 }));
            })
        );
    });

    calculateMealAndHousingStipendAmounts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PayBillingActions.setWeeklyStipends),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectWeeklyStipends),
                this.store.select(Selectors.selectIsAyaHousing),
                this.store.select(Selectors.selectMaxMealStipend),
                this.store.select(Selectors.selectMaxHousingStipend),
                this._featureManager.isEnabled(FeatureFlag.RoundStipendsFix)
            ]),
            switchMap(([, weeklyStipends, isAyaHousing, maxMealStipend, maxHousingStipend, roundStipendsFix]) => {
                if (isAyaHousing) {
                    const mealStipend = roundStipendsFix
                        ? weeklyStipends
                        : new Big(weeklyStipends).div(7).round(2, Big.roundHalfUp).mul(7).toNumber();

                    return of(
                        PayBillingActions.setCalculatedStipendValues({
                            mealStipend,
                            housingStipend: 0,
                            weeklyStipends: mealStipend
                        })
                    );
                }

                const { mealStipend, housingStipend } =
                    PayBillingStateHelpers.calculateMealsAndHousingStipendAmountsFromWeeklyStipend(
                        weeklyStipends,
                        maxMealStipend,
                        maxHousingStipend,
                        roundStipendsFix
                    );

                return of(
                    PayBillingActions.setCalculatedStipendValues({
                        mealStipend,
                        housingStipend,
                        weeklyStipends: new Big(mealStipend).add(housingStipend).toNumber()
                    })
                );
            })
        );
    });

    setMealStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                PayBillingActions.setMealStipend,
                PayBillingActions.setCalculatedStipendValues,
                PageActions.setShifts,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMealStipend),
                this.store.select(Selectors.selectHousingStipend),
                this.store.select(Selectors.selectShifts),
                this.store.select(Selectors.selectIsPipelined),
                this._featureManager.isEnabled(FeatureFlag.RoundStipendsFix)
            ]),
            switchMap(([, mealStipend, housingStipend, shifts, pipelined, roundStipendsFix]) => {
                // changing meal stipend should not change the housing stipend
                if (!roundStipendsFix) {
                    mealStipend = PayBillingStateHelpers.getWeeklyStipends(mealStipend);
                } else if (!pipelined) {
                    mealStipend = new Big(mealStipend).round(0, Big.roundHalfUp).toNumber();
                }

                const perDay = PayBillingStateHelpers.getStipendsPerDay(mealStipend);
                const perShift = PayBillingStateHelpers.getStipendsPerShift(perDay, shifts);
                return of(
                    PayBillingActions.setMealStipendAndRelatedValues({
                        mealStipend: mealStipend,
                        mealStipendPerShift: perShift,
                        mealStipendPerDay: perDay,
                        weeklyStipends: PayBillingStateHelpers.getTotalStipends(mealStipend, housingStipend)
                    })
                );
            })
        );
    });

    setHousingStipend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                PayBillingActions.setHousingStipend,
                PayBillingActions.setCalculatedStipendValues,
                PageActions.setShifts,
                MarginCalculatorApiActions.marginLoadedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectMealStipend),
                this.store.select(Selectors.selectHousingStipend),
                this.store.select(Selectors.selectShifts),
                this.store.select(Selectors.selectIsPipelined),
                this._featureManager.isEnabled(FeatureFlag.RoundStipendsFix)
            ]),
            switchMap(([, mealStipend, housingStipend, shifts, pipelined, roundStipendsFix]) => {
                // changing housing stipend should not change the meal stipend
                if (!pipelined && roundStipendsFix) {
                    housingStipend = new Big(housingStipend).round(0, Big.roundHalfUp).toNumber();
                }
                const perDay = PayBillingStateHelpers.getStipendsPerDay(housingStipend);
                const perShift = PayBillingStateHelpers.getStipendsPerShift(perDay, shifts);
                return of(
                    PayBillingActions.setHousingStipendAndRelatedValues({
                        housingStipend: housingStipend,
                        housingStipendPerShift: perShift,
                        housingStipendPerDay: perDay,
                        weeklyStipends: PayBillingStateHelpers.getTotalStipends(mealStipend, housingStipend)
                    })
                );
            })
        );
    });

    setOvertimeRules$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ApiActions.billingRatesLoadedSuccess,
                PageActions.setShiftHours,
                PayBillingActions.setOtBillingRulesEnabled,
                ComputationActions.billingOvertimeEnabledComputedSuccess,
                PageActions.baseBillingRateChanged,
                PageActions.setBaseBillingRate,
                ComputationActions.baseBillingRateComputedSuccess,
                PageActions.removeLockDownLogic
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectFacilityOvertimeInfo),
                this.store.select(Selectors.selectIsMarginCreatedFromJob)
            ]),
            switchMap(([, overtimeInfo, isMarginCreatedFromJob]) => {
                if (this._marginFormService?.parentForm?.dirty || isMarginCreatedFromJob) {
                    return of(
                        PayBillingActions.overtimeRulesChanged({
                            overtimeHours: overtimeInfo.firstOvertimeHours,
                            billDailyOtAfter: overtimeInfo.secondOvertimeHours,
                            billDorW: overtimeInfo.firstOvertimeFrequency,
                            billDorWM: overtimeInfo.secondOvertimeFrequency,
                            andOr: overtimeInfo.andOr
                        })
                    );
                }

                return of(PageActions.noOp({ debug: 'setOvertimeRules$ effect' }));
            })
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _featureManager: LDFeatureManager
    ) {}
}
